import {
  AutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import {
  Switch,
  FormControlLabel,
  Button,
  MenuItem,
  Grid,
  Typography
} from '@material-ui/core'
import React, { useEffect } from 'react'
import { toast } from 'react-toastify'

import SectionTitle from '../../../components/SectionTitle'
import { ENTITY_LIST_QUERY } from '../../../graphql/entityList'
import {
  addCommaToPrice,
  getOrdinal,
  removeCommaFromPrice
} from '../../../helpers/numbers'
import { getTranslate, lang } from '../../../localization'
import { EntityList } from '../../../types/entityList'
import { EntityType } from '../../../types/reserveMonitoringList'
import { WalletInput, WalletEntity } from '../../../types/wallet'
import useStyles from './styles/stepTwoCreditorsDetails.styles'

interface StepTwoCreditorsDetailsProps {
  walletInput: WalletInput
  setwalletInput: React.Dispatch<React.SetStateAction<WalletInput>>
}

const StepTwoCreditorsDetails: React.FC<StepTwoCreditorsDetailsProps> = ({
  walletInput,
  setwalletInput
}) => {
  const classes = useStyles()
  const [options, setOptions] = React.useState<EntityList[]>([])

  const [getEntityList, { loading: loadingForEntityList }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    onCompleted: (res) => {
      setOptions(res.entityList)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  // Function to handle adding a new creditor
  const addCreditor = () => {
    setwalletInput((prev) => ({
      ...prev,
      creditors: [
        ...prev.creditors,
        {
          entity: { entityType: '', entityID: '' },
          maxCredit: 0,
          maxReservable: 0,
          active: true,
          canUseCredit: true,
          autoSettelmentEnabled: true,
          paymentTermAssignment: []
        }
      ]
    }))
  }

  // Function to handle changes in each creditor
  const handleCreditorChange = (index: number, field: string, value: any) => {
    const updatedCreditors = [...walletInput.creditors]
    updatedCreditors[index] = {
      ...updatedCreditors[index],
      [field]: value
    }
    setwalletInput((prev) => ({
      ...prev,
      creditors: updatedCreditors
    }))
  }

  // Function to handle changes in entity fields within each creditor
  const handleCreditorEntityChange = (index: number, entity: WalletEntity) => {
    const updatedCreditors = [...walletInput.creditors]
    updatedCreditors[index] = {
      ...updatedCreditors[index],
      entity
    }
    setwalletInput((prev) => ({
      ...prev,
      creditors: updatedCreditors
    }))
  }

  useEffect(() => {
    // Fetch options when entityType is set in any creditor
    walletInput.creditors.forEach((creditor) => {
      if (creditor.entity?.entityType) {
        getEntityList({
          variables: {
            entityType: creditor.entity.entityType
          }
        })
      }
    })
  }, [walletInput.creditors])

  return (
    <div className={classes.container}>
      {walletInput.creditors.map((creditor, index) => (
        <div key={index} className={classes.creditorSection}>
          <SectionTitle
            title={`${getTranslate('Creditor')} ${getOrdinal(index, lang)}`}
            marginBottom
          />

          <Grid container spacing={3}>
            {/* Entity Dropdown */}
            <Grid item xs={12} sm={4}>
              <SelectWithLabel
                label={getTranslate('Entity Type')}
                placeholder={getTranslate('Select the Entity Type')}
                fullWidth
                value={creditor.entity.entityType}
                onChange={(event) => {
                  handleCreditorEntityChange(index, {
                    ...creditor.entity,
                    entityType: event.target.value as EntityType,
                    entityCode: '',
                    entityID: '',
                    entityName: ''
                  })
                }}
                size="small">
                {/*
                  Provider only — the same rule as WalletCreditorForm's own entity select.
                  Credit on a bilateral wallet is extended BY the provider whose rate plan is
                  being booked, and the wallet service now refuses a non-Provider creditor,
                  so offering Channel here would produce a form that fails on submit.

                  This form is a near-duplicate of
                  WalletCreditorForm/steps/creditorDetailsSection.tsx with independent state
                  handling, which is exactly why the option survived here after being removed
                  there. See the refactor note in that file.
                */}
                <MenuItem
                  key="Provider"
                  value="Provider"
                  classes={{
                    root: lang === 'fa' ? classes.menuItem : undefined
                  }}>
                  {getTranslate('Provider')}
                </MenuItem>
              </SelectWithLabel>
            </Grid>

            {/* Entity Autocomplete */}
            <Grid item xs={12} sm={4}>
              <AutocompleteWithLabel
                options={loadingForEntityList ? [] : options}
                inputSize="small"
                getOptionLabel={(option) => option.label}
                getOptionSelected={(option, value) =>
                  option.label.toLowerCase().includes(value.label.toLowerCase())
                }
                onChange={(_, value: any) => {
                  handleCreditorEntityChange(index, {
                    ...creditor.entity,
                    entityID: value ? value.id : '',
                    entityCode: value ? value.entityCode : '',
                    entityName: value ? value.label : ''
                  })
                }}
                label={getTranslate('Entity')}
                loading={loadingForEntityList}
                loadingText={getTranslate('Loading') + '...'}
              />
            </Grid>
            {/* Currency Display */}
            <Grid item xs={12} sm={2} className={classes.currencyDisplay}>
              <Typography variant="body2" color="textSecondary">
                {getTranslate('Currency')}
              </Typography>
              <Typography variant="body1" color="primary">
                {getTranslate(walletInput.currency || '')}
              </Typography>
            </Grid>

            {/* Max Credit Input */}
            <Grid item xs={12} sm={4}>
              <InputWithLabel
                label={getTranslate('Max Credit')}
                placeholder={getTranslate('Enter max credit')}
                autoComplete="off"
                value={addCommaToPrice(creditor.maxCredit)}
                onChange={(event) => {
                  const commaLessValue = removeCommaFromPrice(
                    event.currentTarget.value
                  )
                  handleCreditorChange(
                    index,
                    'maxCredit',
                    Number(commaLessValue)
                  )
                }}
                inputSize="small"
              />
            </Grid>

            {/* Max Reservable Input */}
            <Grid item xs={12} sm={4}>
              <InputWithLabel
                label={getTranslate('Max Reservable')}
                placeholder={getTranslate('Enter max reservable')}
                autoComplete="off"
                value={addCommaToPrice(creditor.maxReservable)}
                onChange={(event) => {
                  const commaLessValue = removeCommaFromPrice(
                    event.currentTarget.value
                  )
                  handleCreditorChange(
                    index,
                    'maxReservable',
                    Number(commaLessValue)
                  )
                }}
                inputSize="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}></Grid>

            {/* Active Toggle */}
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={creditor.active}
                    onChange={() =>
                      handleCreditorChange(index, 'active', !creditor.active)
                    }
                    color="primary"
                  />
                }
                label={getTranslate('Active')}
              />
            </Grid>

            {/* Can Use Credit Toggle */}
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={creditor.canUseCredit}
                    onChange={() =>
                      handleCreditorChange(
                        index,
                        'canUseCredit',
                        !creditor.canUseCredit
                      )
                    }
                    color="primary"
                  />
                }
                label={getTranslate('Can use credit')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={creditor.autoSettelmentEnabled}
                    onChange={() =>
                      handleCreditorChange(
                        index,
                        'autoSettelmentEnabled',
                        !creditor.autoSettelmentEnabled
                      )
                    }
                    color="primary"
                  />
                }
                label={getTranslate('Enable Auto Settlement')}
              />
            </Grid>
          </Grid>
        </div>
      ))}

      {/* Add More Creditor Button */}
      <Button
        className={classes.addCreditorButton}
        color="primary"
        onClick={addCreditor}
        startIcon={<span className={classes.addIcon}>+</span>}>
        {getTranslate('Add More Creditor')}
      </Button>
    </div>
  )
}

export default StepTwoCreditorsDetails
